import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Search, Archive, Trash2, RotateCcw, MessageSquare, BookOpen, Users, GraduationCap } from "lucide-react";
import { format } from "date-fns";

type DiscussionType = "course" | "general" | "mentor";

interface Discussion {
  id: string;
  title: string;
  content: string;
  created_at: string;
  is_archived: boolean;
  archived_at: string | null;
  user_id: string;
  user?: { full_name: string | null };
  reply_count?: number;
  type: DiscussionType;
}

interface Reply {
  id: string;
  content: string;
  created_at: string;
  is_archived: boolean;
  discussion_id: string;
  user_id: string;
  user?: { full_name: string | null };
  type: DiscussionType;
}

export function DiscussionModerationManager() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"active" | "archived">("active");
  const [discussionType, setDiscussionType] = useState<DiscussionType>("course");
  const [confirmAction, setConfirmAction] = useState<{
    type: "archive" | "unarchive" | "delete";
    target: "discussion" | "reply";
    item: Discussion | Reply;
  } | null>(null);

  // Fetch discussions based on type
  const { data: discussions, isLoading: loadingDiscussions } = useQuery({
    queryKey: ["admin-discussions", discussionType, activeTab],
    queryFn: async () => {
      const isArchived = activeTab === "archived";
      
      type DiscussionRow = {
        id: string;
        title: string;
        content: string;
        created_at: string;
        is_archived: boolean | null;
        archived_at: string | null;
        user_id: string;
      };

      let data: DiscussionRow[] | null = null;
      let error: Error | null = null;
      
      if (discussionType === "course") {
        const result = await supabase
          .from("course_discussions")
          .select("id, title, content, created_at, is_archived, archived_at, user_id")
          .eq("is_archived", isArchived)
          .order("created_at", { ascending: false });
        data = result.data;
        error = result.error;
      } else if (discussionType === "general") {
        const result = await supabase
          .from("general_discussions")
          .select("id, title, content, created_at, is_archived, archived_at, user_id")
          .eq("is_archived", isArchived)
          .order("created_at", { ascending: false });
        data = result.data;
        error = result.error;
      } else {
        const result = await supabase
          .from("mentor_discussions")
          .select("id, title, content, created_at, is_archived, archived_at, user_id")
          .eq("is_archived", isArchived)
          .order("created_at", { ascending: false });
        data = result.data;
        error = result.error;
      }

      if (error) throw error;

      // Get user names and reply counts
      const userIds: string[] = [...new Set((data || []).map(d => d.user_id).filter(Boolean))];
      const discussionIds: string[] = (data || []).map(d => d.id).filter(Boolean);

      const usersResult = await supabase.from("profiles").select("id, full_name").in("id", userIds.length > 0 ? userIds : ['']);
      
      let repliesResult: { data: { discussion_id: string }[] | null } = { data: [] };
      if (discussionIds.length > 0) {
        if (discussionType === "course") {
          repliesResult = await supabase.from("discussion_replies").select("discussion_id").in("discussion_id", discussionIds);
        } else if (discussionType === "general") {
          repliesResult = await supabase.from("general_discussion_replies").select("discussion_id").in("discussion_id", discussionIds);
        } else {
          repliesResult = await supabase.from("mentor_discussion_replies").select("discussion_id").in("discussion_id", discussionIds);
        }
      }

      const usersMap = new Map((usersResult.data || []).map(u => [u.id, u.full_name]));
      const replyCounts = (repliesResult.data || []).reduce((acc, r) => {
        acc[r.discussion_id] = (acc[r.discussion_id] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      return (data || []).map(d => ({
        ...d,
        is_archived: d.is_archived ?? false,
        type: discussionType,
        user: { full_name: usersMap.get(d.user_id) || "Unknown" },
        reply_count: replyCounts[d.id] || 0,
      })) as Discussion[];
    },
    enabled: !!user,
  });

  // Fetch replies for archived view
  const { data: replies, isLoading: loadingReplies } = useQuery({
    queryKey: ["admin-replies", discussionType, activeTab],
    queryFn: async () => {
      if (activeTab !== "archived") return [];
      
      type ReplyRow = {
        id: string;
        content: string;
        created_at: string;
        is_archived: boolean | null;
        discussion_id: string;
        user_id: string;
      };

      let data: ReplyRow[] | null = null;
      let error: Error | null = null;

      if (discussionType === "course") {
        const result = await supabase
          .from("discussion_replies")
          .select("id, content, created_at, is_archived, discussion_id, user_id")
          .eq("is_archived", true)
          .order("created_at", { ascending: false });
        data = result.data;
        error = result.error;
      } else if (discussionType === "general") {
        const result = await supabase
          .from("general_discussion_replies")
          .select("id, content, created_at, is_archived, discussion_id, user_id")
          .eq("is_archived", true)
          .order("created_at", { ascending: false });
        data = result.data;
        error = result.error;
      } else {
        const result = await supabase
          .from("mentor_discussion_replies")
          .select("id, content, created_at, is_archived, discussion_id, user_id")
          .eq("is_archived", true)
          .order("created_at", { ascending: false });
        data = result.data;
        error = result.error;
      }

      if (error) throw error;

      const userIds: string[] = [...new Set((data || []).map(r => r.user_id).filter(Boolean))];
      const usersResult = await supabase.from("profiles").select("id, full_name").in("id", userIds.length > 0 ? userIds : ['']);
      const usersMap = new Map((usersResult.data || []).map(u => [u.id, u.full_name]));

      return (data || []).map(r => ({
        ...r,
        is_archived: r.is_archived ?? false,
        type: discussionType,
        user: { full_name: usersMap.get(r.user_id) || "Unknown" },
      })) as Reply[];
    },
    enabled: !!user && activeTab === "archived",
  });

  const archiveDiscussion = useMutation({
    mutationFn: async ({ id, type }: { id: string; type: DiscussionType }) => {
      const table = type === "course" ? "course_discussions" : type === "general" ? "general_discussions" : "mentor_discussions";
      const { error } = await supabase
        .from(table)
        .update({ is_archived: true, archived_at: new Date().toISOString(), archived_by: user?.id })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-discussions"] });
      toast({ title: "Discussion archived" });
    },
    onError: () => toast({ title: "Failed to archive", variant: "destructive" }),
  });

  const unarchiveDiscussion = useMutation({
    mutationFn: async ({ id, type }: { id: string; type: DiscussionType }) => {
      const table = type === "course" ? "course_discussions" : type === "general" ? "general_discussions" : "mentor_discussions";
      const { error } = await supabase
        .from(table)
        .update({ is_archived: false, archived_at: null, archived_by: null })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-discussions"] });
      toast({ title: "Discussion restored" });
    },
    onError: () => toast({ title: "Failed to restore", variant: "destructive" }),
  });

  const deleteDiscussion = useMutation({
    mutationFn: async ({ id, type }: { id: string; type: DiscussionType }) => {
      const table = type === "course" ? "course_discussions" : type === "general" ? "general_discussions" : "mentor_discussions";
      const { error } = await supabase.from(table).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-discussions"] });
      toast({ title: "Discussion deleted permanently" });
    },
    onError: () => toast({ title: "Failed to delete", variant: "destructive" }),
  });

  const archiveReply = useMutation({
    mutationFn: async ({ id, type }: { id: string; type: DiscussionType }) => {
      const table = type === "course" ? "discussion_replies" : type === "general" ? "general_discussion_replies" : "mentor_discussion_replies";
      const { error } = await supabase
        .from(table)
        .update({ is_archived: true, archived_at: new Date().toISOString(), archived_by: user?.id })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-replies"] });
      toast({ title: "Reply archived" });
    },
    onError: () => toast({ title: "Failed to archive", variant: "destructive" }),
  });

  const unarchiveReply = useMutation({
    mutationFn: async ({ id, type }: { id: string; type: DiscussionType }) => {
      const table = type === "course" ? "discussion_replies" : type === "general" ? "general_discussion_replies" : "mentor_discussion_replies";
      const { error } = await supabase
        .from(table)
        .update({ is_archived: false, archived_at: null, archived_by: null })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-replies"] });
      toast({ title: "Reply restored" });
    },
    onError: () => toast({ title: "Failed to restore", variant: "destructive" }),
  });

  const deleteReply = useMutation({
    mutationFn: async ({ id, type }: { id: string; type: DiscussionType }) => {
      const table = type === "course" ? "discussion_replies" : type === "general" ? "general_discussion_replies" : "mentor_discussion_replies";
      const { error } = await supabase.from(table).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-replies"] });
      toast({ title: "Reply deleted permanently" });
    },
    onError: () => toast({ title: "Failed to delete", variant: "destructive" }),
  });

  const handleConfirmAction = () => {
    if (!confirmAction) return;
    const { type, target, item } = confirmAction;
    
    if (target === "discussion") {
      const discussion = item as Discussion;
      if (type === "archive") archiveDiscussion.mutate({ id: discussion.id, type: discussion.type });
      else if (type === "unarchive") unarchiveDiscussion.mutate({ id: discussion.id, type: discussion.type });
      else deleteDiscussion.mutate({ id: discussion.id, type: discussion.type });
    } else {
      const reply = item as Reply;
      if (type === "archive") archiveReply.mutate({ id: reply.id, type: reply.type });
      else if (type === "unarchive") unarchiveReply.mutate({ id: reply.id, type: reply.type });
      else deleteReply.mutate({ id: reply.id, type: reply.type });
    }
    setConfirmAction(null);
  };

  const filteredDiscussions = (discussions || []).filter(d =>
    d.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.user?.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredReplies = (replies || []).filter(r =>
    r.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.user?.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isLoading = loadingDiscussions || loadingReplies;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Discussion Moderation
        </CardTitle>
        <CardDescription>
          Archive or delete discussions and comments. Archived content is hidden from users but retained.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Discussion Type Selector */}
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={discussionType === "course" ? "default" : "outline"}
            size="sm"
            onClick={() => setDiscussionType("course")}
          >
            <BookOpen className="h-4 w-4 mr-2" />
            Course
          </Button>
          <Button
            variant={discussionType === "general" ? "default" : "outline"}
            size="sm"
            onClick={() => setDiscussionType("general")}
          >
            <Users className="h-4 w-4 mr-2" />
            General
          </Button>
          <Button
            variant={discussionType === "mentor" ? "default" : "outline"}
            size="sm"
            onClick={() => setDiscussionType("mentor")}
          >
            <GraduationCap className="h-4 w-4 mr-2" />
            Mentor
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search discussions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "active" | "archived")}>
          <TabsList>
            <TabsTrigger value="active">Active</TabsTrigger>
            <TabsTrigger value="archived">Archived</TabsTrigger>
          </TabsList>

          <TabsContent value="active">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredDiscussions.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">No active discussions found.</p>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {filteredDiscussions.map((discussion) => (
                    <div key={discussion.id} className="border rounded-lg p-4 space-y-2">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium truncate">{discussion.title}</h4>
                          <p className="text-sm text-muted-foreground line-clamp-2">{discussion.content}</p>
                          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                            <span>By {discussion.user?.full_name}</span>
                            <span>•</span>
                            <span>{format(new Date(discussion.created_at), "MMM d, yyyy")}</span>
                            <span>•</span>
                            <span>{discussion.reply_count} replies</span>
                          </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setConfirmAction({ type: "archive", target: "discussion", item: discussion })}
                          >
                            <Archive className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setConfirmAction({ type: "delete", target: "discussion", item: discussion })}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </TabsContent>

          <TabsContent value="archived">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-4">
                  {/* Archived Discussions */}
                  {filteredDiscussions.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm text-muted-foreground">Archived Discussions</h4>
                      {filteredDiscussions.map((discussion) => (
                        <div key={discussion.id} className="border rounded-lg p-4 bg-muted/50 space-y-2">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium truncate">{discussion.title}</h4>
                                <Badge variant="secondary">Archived</Badge>
                              </div>
                              <p className="text-sm text-muted-foreground line-clamp-2">{discussion.content}</p>
                              <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                                <span>By {discussion.user?.full_name}</span>
                                {discussion.archived_at && (
                                  <>
                                    <span>•</span>
                                    <span>Archived {format(new Date(discussion.archived_at), "MMM d, yyyy")}</span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2 shrink-0">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setConfirmAction({ type: "unarchive", target: "discussion", item: discussion })}
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => setConfirmAction({ type: "delete", target: "discussion", item: discussion })}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Archived Replies */}
                  {filteredReplies.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm text-muted-foreground">Archived Replies</h4>
                      {filteredReplies.map((reply) => (
                        <div key={reply.id} className="border rounded-lg p-4 bg-muted/50 space-y-2">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary">Reply</Badge>
                                <Badge variant="outline">Archived</Badge>
                              </div>
                              <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{reply.content}</p>
                              <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                                <span>By {reply.user?.full_name}</span>
                              </div>
                            </div>
                            <div className="flex gap-2 shrink-0">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setConfirmAction({ type: "unarchive", target: "reply", item: reply })}
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => setConfirmAction({ type: "delete", target: "reply", item: reply })}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {filteredDiscussions.length === 0 && filteredReplies.length === 0 && (
                    <p className="text-center py-8 text-muted-foreground">No archived content found.</p>
                  )}
                </div>
              </ScrollArea>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Confirmation Dialog */}
      <AlertDialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === "archive" && "Archive this content?"}
              {confirmAction?.type === "unarchive" && "Restore this content?"}
              {confirmAction?.type === "delete" && "Permanently delete this content?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === "archive" && "This will hide the content from all non-admin users. It can be restored later."}
              {confirmAction?.type === "unarchive" && "This will make the content visible to all users again."}
              {confirmAction?.type === "delete" && "This action cannot be undone. The content will be permanently removed."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAction}
              className={confirmAction?.type === "delete" ? "bg-destructive hover:bg-destructive/90" : ""}
            >
              {confirmAction?.type === "archive" && "Archive"}
              {confirmAction?.type === "unarchive" && "Restore"}
              {confirmAction?.type === "delete" && "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}